'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── RESOURCE DISTRIBUTION TRACKING ──────────────────────────────────────────
// Real-time allocation maps, volunteer assignment, ward disparity matching,
// observation-triggered logistics, community impact measurement dashboard

interface Ward {
  id: string;
  name: string;
  city: string;
  severity: 'CRITICAL' | 'HIGH' | 'MODERATE';
  needs: { type: string; amount: number; unit: string; nameOfAllah: string }[];
  resources: { type: string; available: number; unit: string }[];
  volunteers: number;
  activeQuests: number;
  impactScore: number;
  x: number;
  y: number;
}

interface Volunteer {
  id: string;
  name: string;
  city: string;
  ward: string;
  skills: string[];
  availability: 'available' | 'assigned' | 'offline';
  assignedQuest?: string;
  completedQuests: number;
}

interface ResourceAllocation {
  id: string;
  type: string;
  from: string;
  to: string;
  amount: number;
  unit: string;
  status: 'pending' | 'in-transit' | 'delivered';
  triggeredBy: 'observation' | 'quest' | 'manual';
  timestamp: string;
}

interface ImpactMetric {
  label: string;
  value: number;
  unit: string;
  change: number;
  color: string;
  nameOfAllah: string;
  arabicName: string;
}

const WARDS: Ward[] = [
  { id: 'w1', name: 'Englewood', city: 'Chicago', severity: 'CRITICAL', needs: [{ type: 'Food', amount: 500, unit: 'meals/wk', nameOfAllah: 'AR-RAZZAQ' }, { type: 'Health', amount: 200, unit: 'visits/mo', nameOfAllah: 'ASH-SHAFI' }], resources: [{ type: 'Food', available: 180, unit: 'meals/wk' }, { type: 'Volunteers', available: 8, unit: 'active' }], volunteers: 8, activeQuests: 4, impactScore: 42, x: 35, y: 45 },
  { id: 'w2', name: 'South Shore', city: 'Chicago', severity: 'HIGH', needs: [{ type: 'Reentry', amount: 30, unit: 'placements/mo', nameOfAllah: 'AL-AFUWW' }], resources: [{ type: 'Training', available: 12, unit: 'slots/mo' }, { type: 'Volunteers', available: 5, unit: 'active' }], volunteers: 5, activeQuests: 3, impactScore: 61, x: 40, y: 52 },
  { id: 'w3', name: 'Brightmoor', city: 'Detroit', severity: 'HIGH', needs: [{ type: 'Food', amount: 300, unit: 'lbs/wk', nameOfAllah: 'AR-RAZZAQ' }, { type: 'Housing', amount: 15, unit: 'units', nameOfAllah: 'AL-WALI' }], resources: [{ type: 'Food', available: 150, unit: 'lbs/wk' }, { type: 'Volunteers', available: 6, unit: 'active' }], volunteers: 6, activeQuests: 2, impactScore: 55, x: 62, y: 30 },
  { id: 'w4', name: 'West End', city: 'Atlanta', severity: 'CRITICAL', needs: [{ type: 'Housing', amount: 40, unit: 'units', nameOfAllah: 'AL-WALI' }, { type: 'Food', amount: 400, unit: 'meals/wk', nameOfAllah: 'AR-RAZZAQ' }], resources: [{ type: 'Food', available: 220, unit: 'meals/wk' }, { type: 'Volunteers', available: 11, unit: 'active' }], volunteers: 11, activeQuests: 5, impactScore: 48, x: 55, y: 68 },
  { id: 'w5', name: 'East Portland', city: 'Portland', severity: 'CRITICAL', needs: [{ type: 'Housing', amount: 25, unit: 'units', nameOfAllah: 'AL-WALI' }, { type: 'Community', amount: 100, unit: 'connections/mo', nameOfAllah: 'AL-WADUD' }], resources: [{ type: 'Community', available: 45, unit: 'connections/mo' }, { type: 'Volunteers', available: 4, unit: 'active' }], volunteers: 4, activeQuests: 2, impactScore: 38, x: 12, y: 25 },
];

const VOLUNTEERS: Volunteer[] = [
  { id: 'v1', name: 'Caretaker A', city: 'Chicago', ward: 'Englewood', skills: ['Food Distribution', 'IRL Lens', 'Community Outreach'], availability: 'available', completedQuests: 12 },
  { id: 'v2', name: 'Caretaker B', city: 'Chicago', ward: 'South Shore', skills: ['Reentry Support', 'Training', 'Legal Aid'], availability: 'assigned', assignedQuest: 'Iftar on Wheels', completedQuests: 8 },
  { id: 'v3', name: 'Caretaker C', city: 'Detroit', ward: 'Brightmoor', skills: ['Urban Farming', 'Food Production', 'Youth Programs'], availability: 'available', completedQuests: 15 },
  { id: 'v4', name: 'Caretaker D', city: 'Atlanta', ward: 'West End', skills: ['Housing Advocacy', 'Mutual Aid', 'Community Organizing'], availability: 'assigned', assignedQuest: 'Ward Resource Bridge', completedQuests: 6 },
  { id: 'v5', name: 'Caretaker E', city: 'Portland', ward: 'East Portland', skills: ['Refugee Support', 'Community Building', 'IRL Lens'], availability: 'offline', completedQuests: 4 },
];

const ALLOCATIONS: ResourceAllocation[] = [
  { id: 'a1', type: 'Food', from: 'IMAN Chicago', to: 'Englewood', amount: 200, unit: 'meals', status: 'in-transit', triggeredBy: 'observation', timestamp: '2 hrs ago' },
  { id: 'a2', type: 'Training', from: 'South Shore', to: 'Englewood', amount: 5, unit: 'slots', status: 'delivered', triggeredBy: 'quest', timestamp: '1 day ago' },
  { id: 'a3', type: 'Seeds', from: 'Brightmoor', to: 'Hamtramck', amount: 50, unit: 'lbs', status: 'pending', triggeredBy: 'manual', timestamp: '30 min ago' },
  { id: 'a4', type: 'Food', from: 'Atlanta Hub', to: 'West End', amount: 300, unit: 'meals', status: 'delivered', triggeredBy: 'quest', timestamp: '3 hrs ago' },
];

const IMPACT_METRICS: ImpactMetric[] = [
  { label: 'Meals Distributed', value: 1240, unit: 'this week', change: 18, color: '#2d7a47', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق' },
  { label: 'Volunteers Active', value: 34, unit: 'across 4 cities', change: 5, color: '#2d8a87', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود' },
  { label: 'Quests Completed', value: 47, unit: 'this month', change: 12, color: '#c9a84c', nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط' },
  { label: 'Observations Logged', value: 183, unit: 'total', change: 24, color: '#8a8ab8', nameOfAllah: 'AL-KHABIR', arabicName: 'الْخَبِير' },
  { label: 'Reentry Placements', value: 28, unit: 'this month', change: 3, color: '#E05252', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ' },
  { label: 'Ward Gaps Bridged', value: 15, unit: 'active bridges', change: 4, color: '#2d7a47', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ' },
];

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#E05252',
  HIGH: '#C9A84C',
  MODERATE: '#7EC8A0',
};

const STATUS_COLORS: Record<string, string> = {
  'pending': '#c9a84c',
  'in-transit': '#2d8a87',
  'delivered': '#2d7a47',
};

const AVAIL_COLORS: Record<string, string> = {
  available: '#2d7a47',
  assigned: '#c9a84c',
  offline: 'rgba(212,197,169,0.2)',
};

type DashTab = 'overview' | 'allocation' | 'volunteers' | 'disparities' | 'impact';

export default function ResourceDistributionDashboard() {
  const [activeTab, setActiveTab] = useState<DashTab>('overview');
  const [selectedWard, setSelectedWard] = useState<Ward | null>(null);
  const [ticker, setTicker] = useState(0);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => setTicker(t => t + 1), 5000);
    return () => clearInterval(interval);
  }, []);

  const TABS: { key: DashTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: '◎' },
    { key: 'allocation', label: 'Allocation Map', icon: '⬡' },
    { key: 'volunteers', label: 'Volunteers', icon: '🤝' },
    { key: 'disparities', label: 'Ward Disparities', icon: '⚖' },
    { key: 'impact', label: 'Impact', icon: '✦' },
  ];

  return (
    <section style={{ background: '#060d12', borderTop: '1px solid rgba(45,122,71,0.1)', padding: '60px 0' }} id="resource-distribution">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 8 }}>
            ⬡ RESOURCE DISTRIBUTION · تَوْزِيع الْمَوَارِد
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(16px, 3vw, 22px)', color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                Resource Distribution Tracking
              </h2>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6, maxWidth: 500 }}>
                Real-time allocation maps, volunteer assignment, ward disparity matching, observation-triggered logistics, and community impact measurement.
              </p>
            </div>
            {/* Live indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2d7a47', animation: 'pulse 2s ease-in-out infinite', display: 'block' }} />
              <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase' }}>Live · {ticker > 0 ? 'Updated' : 'Syncing'}</span>
            </div>
          </div>
        </div>

        {/* Impact metrics row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 24 }}>
          {IMPACT_METRICS.slice(0, 6).map((metric, i) => (
            <div key={i} style={{ padding: '12px 14px', background: 'rgba(6,13,18,0.8)', border: `1px solid ${metric.color}15`, borderRadius: 6, borderTop: `2px solid ${metric.color}40` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: metric.color, lineHeight: 1 }}>{metric.value.toLocaleString()}</p>
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', padding: '1px 5px', borderRadius: 10 }}>+{metric.change}</span>
              </div>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>{metric.label}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.25)' }}>{metric.unit}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 6, color: `${metric.color}60`, letterSpacing: '.06em' }}>{metric.nameOfAllah}</span>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 8, color: `${metric.color}40`, direction: 'rtl' }}>{metric.arabicName}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid rgba(45,122,71,0.12)', marginBottom: 20 }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: '8px 14px', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab.key ? '#2d7a47' : 'transparent'}`, color: activeTab === tab.key ? '#2d7a47' : 'rgba(45,122,71,0.35)', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Recent allocations */}
            <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Recent Allocations</p>
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.3)' }}>{ALLOCATIONS.length} active</span>
              </div>
              {ALLOCATIONS.map(alloc => (
                <div key={alloc.id} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.06)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[alloc.status], flexShrink: 0, marginTop: 3 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', fontWeight: 700 }}>{alloc.amount} {alloc.unit}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: STATUS_COLORS[alloc.status], background: `${STATUS_COLORS[alloc.status]}12`, padding: '1px 5px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase' }}>{alloc.status}</span>
                    </div>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)', marginBottom: 1 }}>{alloc.type} · {alloc.from} → {alloc.to}</p>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.25)' }}>{alloc.timestamp}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 6, color: alloc.triggeredBy === 'observation' ? '#2d8a87' : alloc.triggeredBy === 'quest' ? '#c9a84c' : 'rgba(212,197,169,0.25)', letterSpacing: '.04em' }}>via {alloc.triggeredBy}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Ward overview */}
            <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.1)' }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Ward Status · {WARDS.length} Wards</p>
              </div>
              {WARDS.map(ward => (
                <div key={ward.id} onClick={() => setSelectedWard(selectedWard?.id === ward.id ? null : ward)} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.06)', cursor: 'pointer', background: selectedWard?.id === ward.id ? 'rgba(45,122,71,0.06)' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: SEVERITY_COLORS[ward.severity], background: `${SEVERITY_COLORS[ward.severity]}12`, border: `1px solid ${SEVERITY_COLORS[ward.severity]}25`, padding: '1px 5px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase', flexShrink: 0 }}>{ward.severity}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', flex: 1 }}>{ward.name}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>{ward.city}</span>
                  </div>
                  {/* Impact bar */}
                  <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ height: '100%', width: `${ward.impactScore}%`, background: ward.impactScore > 70 ? '#2d7a47' : ward.impactScore > 50 ? '#c9a84c' : '#E05252', borderRadius: 2, transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.5)' }}>🤝 {ward.volunteers} volunteers</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(201,168,76,0.5)' }}>⌘ {ward.activeQuests} quests</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.3)', marginLeft: 'auto' }}>{ward.impactScore}% impact</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Allocation map tab */}
        {activeTab === 'allocation' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
            {/* SVG allocation map */}
            <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8, overflow: 'hidden', position: 'relative', minHeight: 360 }}>
              <svg width="100%" height="360" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                {/* Grid */}
                {Array.from({ length: 10 }).map((_, i) => (
                  <React.Fragment key={i}>
                    <line x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="rgba(45,122,71,0.04)" strokeWidth="0.3" />
                    <line x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="rgba(45,122,71,0.04)" strokeWidth="0.3" />
                  </React.Fragment>
                ))}
                {/* Allocation flows */}
                {ALLOCATIONS.map((alloc, i) => {
                  const fromWard = WARDS.find(w => w.name === alloc.to);
                  if (!fromWard) return null;
                  const toX = fromWard.x + (i % 3) * 5 - 5;
                  const toY = fromWard.y - 8;
                  return (
                    <g key={alloc.id}>
                      <line x1={toX} y1={toY} x2={fromWard.x} y2={fromWard.y} stroke={STATUS_COLORS[alloc.status]} strokeWidth="0.6" strokeDasharray={alloc.status === 'pending' ? '2,1' : 'none'} opacity="0.6" />
                      <circle cx={toX} cy={toY} r="1.2" fill={STATUS_COLORS[alloc.status]} opacity="0.8" />
                    </g>
                  );
                })}
                {/* Wards */}
                {WARDS.map(ward => (
                  <g key={ward.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedWard(selectedWard?.id === ward.id ? null : ward)}>
                    <circle cx={ward.x} cy={ward.y} r="5" fill={SEVERITY_COLORS[ward.severity]} opacity="0.08" />
                    <circle cx={ward.x} cy={ward.y} r={selectedWard?.id === ward.id ? 3.5 : 2.5} fill={SEVERITY_COLORS[ward.severity]} opacity={selectedWard?.id === ward.id ? 0.9 : 0.5} stroke={selectedWard?.id === ward.id ? '#F5F0E8' : SEVERITY_COLORS[ward.severity]} strokeWidth="0.4" />
                    <text x={ward.x} y={ward.y + 5} textAnchor="middle" fill="rgba(212,197,169,0.4)" fontSize="1.8" fontFamily="monospace">{ward.name.slice(0, 4)}</text>
                  </g>
                ))}
              </svg>
              {/* Legend */}
              <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', gap: 10 }}>
                {Object.entries(STATUS_COLORS).map(([status, color]) => (
                  <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{status}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Allocation list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ALLOCATIONS.map(alloc => (
                <div key={alloc.id} style={{ padding: '10px 12px', background: 'rgba(6,13,18,0.8)', border: `1px solid ${STATUS_COLORS[alloc.status]}20`, borderRadius: 6, borderLeft: `3px solid ${STATUS_COLORS[alloc.status]}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: STATUS_COLORS[alloc.status] }}>{alloc.amount} {alloc.unit}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: STATUS_COLORS[alloc.status], background: `${STATUS_COLORS[alloc.status]}12`, padding: '1px 5px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase' }}>{alloc.status}</span>
                  </div>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#F5F0E8', marginBottom: 2 }}>{alloc.type}</p>
                  <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', marginBottom: 3 }}>{alloc.from} → {alloc.to}</p>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.25)' }}>{alloc.timestamp}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: alloc.triggeredBy === 'observation' ? '#2d8a87' : '#c9a84c', letterSpacing: '.04em' }}>via {alloc.triggeredBy}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Volunteers tab */}
        {activeTab === 'volunteers' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {VOLUNTEERS.map(vol => (
              <div key={vol.id} style={{ padding: '14px 16px', background: 'rgba(6,13,18,0.8)', border: `1px solid ${AVAIL_COLORS[vol.availability]}20`, borderRadius: 8, borderLeft: `3px solid ${AVAIL_COLORS[vol.availability]}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700, marginBottom: 2 }}>{vol.name}</p>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)' }}>{vol.city} · {vol.ward}</p>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: AVAIL_COLORS[vol.availability], background: `${AVAIL_COLORS[vol.availability]}12`, border: `1px solid ${AVAIL_COLORS[vol.availability]}25`, padding: '2px 7px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase' }}>{vol.availability}</span>
                </div>
                {vol.assignedQuest && (
                  <div style={{ padding: '4px 8px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 4, marginBottom: 6 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: '#c9a84c' }}>⌘ {vol.assignedQuest}</p>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
                  {vol.skills.map((skill, i) => (
                    <span key={i} style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.6)', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.12)', padding: '1px 5px', borderRadius: 3, letterSpacing: '.04em' }}>{skill}</span>
                  ))}
                </div>
                <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)' }}>✓ {vol.completedQuests} quests completed</p>
              </div>
            ))}
          </div>
        )}

        {/* Disparities tab */}
        {activeTab === 'disparities' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {WARDS.map(ward => (
              <div key={ward.id} style={{ padding: '16px', background: 'rgba(6,13,18,0.8)', border: `1px solid ${SEVERITY_COLORS[ward.severity]}15`, borderRadius: 8, borderLeft: `3px solid ${SEVERITY_COLORS[ward.severity]}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: SEVERITY_COLORS[ward.severity], background: `${SEVERITY_COLORS[ward.severity]}12`, border: `1px solid ${SEVERITY_COLORS[ward.severity]}25`, padding: '2px 7px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase' }}>{ward.severity}</span>
                  <h3 style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700 }}>{ward.name}</h3>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>{ward.city}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(224,82,82,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Needs</p>
                    {ward.needs.map((need, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#E05252' }}>{need.amount}</span>
                        <span style={{ color: 'rgba(212,197,169,0.6)', fontSize: 9 }}>{need.type} {need.unit}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(138,138,184,0.5)', marginLeft: 'auto' }}>{need.nameOfAllah}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Available</p>
                    {ward.resources.map((res, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#2d7a47' }}>{res.available}</span>
                        <span style={{ color: 'rgba(212,197,169,0.6)', fontSize: 9 }}>{res.type} {res.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Impact tab */}
        {activeTab === 'impact' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {IMPACT_METRICS.map((metric, i) => (
              <div key={i} style={{ padding: '20px', background: 'rgba(6,13,18,0.8)', border: `1px solid ${metric.color}15`, borderRadius: 8, borderTop: `3px solid ${metric.color}50` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color: metric.color, lineHeight: 1, marginBottom: 4 }}>{metric.value.toLocaleString()}</p>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{metric.label}</p>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)' }}>{metric.unit}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#2d7a47', marginBottom: 2 }}>+{metric.change}</p>
                    <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.4)' }}>this period</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: `${metric.color}06`, border: `1px solid ${metric.color}12`, borderRadius: 4 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${metric.color}80`, letterSpacing: '.08em', textTransform: 'uppercase' }}>{metric.nameOfAllah}</p>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${metric.color}50`, direction: 'rtl' }}>{metric.arabicName}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer CTA */}
        <div style={{ marginTop: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link href="/asbab-quest" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 4, color: '#2d7a47', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
            ⌘ Asbab Quest Interface →
          </Link>
          <Link href="/ward-steward" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 4, color: '#c9a84c', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
            ◈ Ward Steward Dashboard →
          </Link>
        </div>
      </div>
    </section>
  );
}
